import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageFlag from '../common/LanguageFlag';
import PrimaryActionButton from '../common/PrimaryActionButton';
import ScrollableDialog from '../common/ScrollableDialog';
import ServicePackageCheckboxGroup from './ServicePackageCheckboxGroup';
import ConsentFormsSummary from '../consent-forms/ConsentFormsSummary';
import { validateServicePackageForm } from './servicePackageValidation';
import { getLocalizedValue, SUPPORTED_LANGUAGE_CODES } from '../../constants/Languages';
import {
  SERVICE_PACKAGE_MAX_EXPIRES_IN_MONTHS,
  SERVICE_PACKAGE_MAX_QUANTITY,
} from '../../services/servicePackageService';
import { BRAND } from '../../theme/brandTokens';
import { serviceAllowsPlanOrPackage } from '../../utils/serviceValueFormat';
import VatClassificationFields from '../common/VatClassificationFields';
import { readVatClassification } from '../../utils/vatClassification';

const buildLocalizedFields = (supportedLanguages, values = {}) => (
  Object.fromEntries(supportedLanguages.map((language) => [language, values?.[language] || '']))
);

const buildInitialPackage = (supportedLanguages, servicePackage) => ({
  id: servicePackage?.id ?? null,
  name: buildLocalizedFields(supportedLanguages, servicePackage?.name),
  price: servicePackage?.price ?? '',
  quantity: servicePackage?.quantity ?? '',
  expiresInMonths: servicePackage?.expiresInMonths ?? '',
  serviceIds: Array.isArray(servicePackage?.serviceIds) ? [...servicePackage.serviceIds] : [],
  teamMemberIds: Array.isArray(servicePackage?.teamMemberIds) ? [...servicePackage.teamMemberIds] : [],
  ...readVatClassification(servicePackage),
});

const toggleId = (ids, id) => (
  ids.some((currentId) => String(currentId) === String(id))
    ? ids.filter((currentId) => String(currentId) !== String(id))
    : [...ids, id]
);

const buildInputSx = (hasError = false) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : BRAND.line}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.3s ease',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
  lineHeight: 'normal',
  minHeight: 48,
  '&:focus': {
    borderColor: BRAND.accent,
  },
});

const FieldMessage = ({ error, helpText }) => {
  const { t } = useTranslation();

  if (error) {
    return (
      <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
        {t(error)}
      </Typography>
    );
  }

  return helpText ? (
    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
      {helpText}
    </Typography>
  ) : null;
};

const NumberField = ({
  id,
  label,
  required,
  value,
  onChange,
  min,
  max,
  step,
  placeholder,
  error,
  helpText,
  disabled,
}) => (
  <Box>
    <Typography component="label" htmlFor={id} variant="subtitle2" sx={{ mb: 1, fontWeight: 600, display: 'block' }}>
      {required ? `${label} *` : label}
    </Typography>
    <Box
      component="input"
      id={id}
      type="number"
      inputMode={step === 1 ? 'numeric' : 'decimal'}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      min={min}
      max={max}
      step={step}
      placeholder={placeholder}
      disabled={disabled}
      sx={buildInputSx(Boolean(error))}
    />
    <FieldMessage error={error} helpText={helpText} />
  </Box>
);

const ServicePackageEditorDialog = ({
  open,
  mode,
  servicePackage,
  supportedLanguages,
  currency,
  services,
  teamMembers = [],
  consentForms = [],
  saving,
  onClose,
  onSave,
}) => {
  const { t, i18n } = useTranslation();
  const languagesKey = (supportedLanguages?.length ? supportedLanguages : ['en']).join(',');
  const languages = useMemo(() => languagesKey.split(','), [languagesKey]);
  const sortedLanguages = useMemo(
    () => [...languages].sort(
      (a, b) => SUPPORTED_LANGUAGE_CODES.indexOf(a) - SUPPORTED_LANGUAGE_CODES.indexOf(b)
    ),
    [languages]
  );
  const [formData, setFormData] = useState(() => buildInitialPackage(languages, servicePackage));
  const [errors, setErrors] = useState({});
  const [activeLanguage, setActiveLanguage] = useState(() => sortedLanguages[0] || 'en');

  useEffect(() => {
    if (open) {
      setFormData(buildInitialPackage(languages, servicePackage));
      setErrors({});
      setActiveLanguage(sortedLanguages[0] || 'en');
    }
  }, [open, servicePackage, languages, sortedLanguages]);

  const serviceOptions = useMemo(() => (services || []).map((service) => ({
    id: service.id,
    label: getLocalizedValue(service.name, i18n.language, undefined, t('servicePackagesManagement.card.removedService')),
    disabled: !serviceAllowsPlanOrPackage(service),
  })), [i18n.language, services, t]);
  const hasIneligibleServices = serviceOptions.some((option) => option.disabled);

  const teamMemberOptions = useMemo(() => (teamMembers || []).map((member) => ({
    id: member.id,
    label: member.name || member.id,
  })), [teamMembers]);

  const updateField = (field) => (value) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  const handleSubmit = () => {
    const eligibleServiceIds = (formData.serviceIds || []).filter((serviceId) => {
      const service = (services || []).find((item) => item.id === serviceId);
      return service && serviceAllowsPlanOrPackage(service);
    });
    const result = validateServicePackageForm({ ...formData, serviceIds: eligibleServiceIds }, languages);
    setErrors(result.errors);
    if (!result.isValid) {
      return;
    }

    onSave({
      ...formData,
      serviceIds: eligibleServiceIds,
      ...result.values,
      name: buildLocalizedFields(languages, formData.name),
      expiresInMonths: result.values.expiresInMonths ?? '',
    });
  };

  const activeLanguageLabel = t(`common.languages.${activeLanguage}`);

  return (
    <ScrollableDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      title={mode === 'edit'
        ? t('servicePackagesManagement.dialog.editTitle')
        : t('servicePackagesManagement.dialog.createTitle')}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button id="service-package-editor-cancel" onClick={onClose} disabled={saving} color="inherit">
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton id="service-package-editor-save" onClick={handleSubmit} disabled={saving}>
            {saving
              ? t('common.saving')
              : mode === 'edit'
                ? t('servicePackagesManagement.dialog.saveEdit')
                : t('servicePackagesManagement.dialog.saveCreate')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box id="service-package-editor" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: errors.name ? 'error.main' : 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={activeLanguage}
            onChange={(_, nextLanguage) => setActiveLanguage(nextLanguage)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              backgroundColor: '#f8f9fa',
              '& .MuiTab-root': {
                color: '#666',
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 48,
              },
              '& .Mui-selected': {
                color: BRAND.accentText,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: BRAND.accent,
              },
            }}
          >
            {sortedLanguages.map((language) => (
              <Tab
                key={language}
                value={language}
                label={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                    <LanguageFlag languageCode={language} size="1rem" />
                    <Typography variant="body2">{t(`common.languages.${language}`)}</Typography>
                  </Box>
                )}
              />
            ))}
          </Tabs>

          <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Typography
              component="label"
              htmlFor="service-package-name"
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600, display: 'block' }}
            >
              {`${t('servicePackagesManagement.fields.name')} (${activeLanguageLabel}) *`}
            </Typography>
            <Box
              component="input"
              id="service-package-name"
              type="text"
              value={formData.name?.[activeLanguage] || ''}
              onChange={(event) => setFormData((previous) => ({
                ...previous,
                name: {
                  ...previous.name,
                  [activeLanguage]: event.target.value,
                },
              }))}
              disabled={saving}
              sx={buildInputSx(Boolean(errors.name))}
            />
            <FieldMessage error={errors.name} />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          <NumberField
            id="service-package-price"
            label={`${t('servicePackagesManagement.fields.price')} (${currency})`}
            required
            value={formData.price}
            onChange={updateField('price')}
            min={0.01}
            step={0.01}
            error={errors.price}
            disabled={saving}
          />
          <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
            <VatClassificationFields
              idPrefix="service-package-vat"
              value={formData}
              errors={errors}
              onChange={(next) => setFormData((previous) => ({ ...previous, ...next }))}
            />
          </Box>
          <NumberField
            id="service-package-quantity"
            label={t('servicePackagesManagement.fields.quantity')}
            required
            value={formData.quantity}
            onChange={updateField('quantity')}
            min={1}
            max={SERVICE_PACKAGE_MAX_QUANTITY}
            step={1}
            error={errors.quantity}
            helpText={t('servicePackagesManagement.fields.quantityHelp')}
            disabled={saving}
          />
          <NumberField
            id="service-package-expires-in-months"
            label={t('servicePackagesManagement.fields.expiresInMonths')}
            value={formData.expiresInMonths}
            onChange={updateField('expiresInMonths')}
            min={1}
            max={SERVICE_PACKAGE_MAX_EXPIRES_IN_MONTHS}
            step={1}
            placeholder={t('servicePackagesManagement.fields.expiresInMonthsPlaceholder')}
            error={errors.expiresInMonths}
            helpText={t('servicePackagesManagement.fields.expiresInMonthsHelp')}
            disabled={saving}
          />
        </Box>

        <ServicePackageCheckboxGroup
          id="service-package-services"
          title={`${t('servicePackagesManagement.fields.includedServices')} *`}
          helpText={hasIneligibleServices
            ? t('servicesManagement.planOrPackageLocked')
            : t('servicePackagesManagement.fields.includedServicesHelp')}
          emptyText={t('servicePackagesManagement.fields.noServicesAvailable')}
          options={serviceOptions}
          selectedIds={formData.serviceIds}
          onToggle={(serviceId) => setFormData((previous) => ({
            ...previous,
            serviceIds: toggleId(previous.serviceIds, serviceId),
          }))}
          error={errors.serviceIds}
          disabled={saving}
        />

        <ConsentFormsSummary
          id="service-package-consent-forms"
          serviceIds={formData.serviceIds}
          services={services}
          consentForms={consentForms}
        />

        <ServicePackageCheckboxGroup
          id="service-package-team-members"
          title={t('servicePackagesManagement.fields.includedProfessionals')}
          helpText={t('servicePackagesManagement.fields.includedProfessionalsHelp')}
          emptyText={t('servicePackagesManagement.fields.noProfessionalsAvailable')}
          options={teamMemberOptions}
          selectedIds={formData.teamMemberIds}
          onToggle={(teamMemberId) => setFormData((previous) => ({
            ...previous,
            teamMemberIds: toggleId(previous.teamMemberIds, teamMemberId),
          }))}
          disabled={saving}
        />
      </Box>
    </ScrollableDialog>
  );
};

export default ServicePackageEditorDialog;
